import { useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { ErrorNote, useErrorText } from "../components/common.js";
import { AuthHeader } from "../components/Layout.js";
import { useSession } from "../state/session.js";

export function LoginPage() {
  const { t } = useTranslation("web");
  const errorText = useErrorText();
  const { services, signIn, user } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  const returnTo = typeof from === "string" && from.startsWith("/") ? from : "/";

  if (user) return <Navigate to={returnTo} replace />;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function submitCredentials(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const result = await services.api.login(email.trim(), password);
      if (result.status === "ok") signIn(result);
      else setChallengeId(result.challengeId);
    });
  }

  function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!challengeId) return;
    void run(async () => {
      signIn(await services.api.verifyTotp(challengeId, code.trim()));
    });
  }

  return (
    <main className="auth">
      <div className="auth-card card">
        <AuthHeader />
        <h1>{challengeId ? t("login.totpTitle") : t("login.title")}</h1>
        {challengeId ? (
          <form onSubmit={submitCode} className="stack">
            <p className="muted">{t("login.totpHint")}</p>
            <label className="field">
              <span>{t("login.code")}</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
              />
            </label>
            <ErrorNote message={error} />
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t("login.working") : t("login.verify")}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCredentials} className="stack">
            <label className="field">
              <span>{t("login.email")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label className="field">
              <span>{t("login.password")}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <ErrorNote message={error} />
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t("login.working") : t("login.continue")}
            </button>
            <p className="muted">{t("login.inviteHint")}</p>
          </form>
        )}
      </div>
    </main>
  );
}
